/**
 * 防水套餐组合配置 - API
 * 接口文档：防水套餐组合_接口文档.md v1.0
 * 路径前缀：/v1/pure_water_goods_combo
 */
import { baseUrl } from '@/config/env';
import request from '@/router/axios';
import { parseParam } from '@/util/util';
import {
  findWaterProofDynamic,
  findWaterProofDynamicId
} from '@/api/work/waterProof/projectDynamic';

// ────────────────────────────────────────────────────
// 套餐组合 CRUD（真实接口）
// ────────────────────────────────────────────────────

/**
 * 分页查询套餐组合列表
 * GET /v1/pure_water_goods_combo
 * @param {Object} params - { rootDepartId, name, page(0-based), size, sort }
 */
export const findComboList = (params) => {
  const query = parseParam(params);
  return request({
    url: `${baseUrl}/joins-sales-cloud/v1/pure_water_goods_combo?` + query,
    method: 'GET'
  });
};

/**
 * 获取套餐组合详情
 * GET /v1/pure_water_goods_combo/{id}
 * @param {Number|String} id
 */
export const getComboDetail = (id) => {
  return request({
    url: `${baseUrl}/joins-sales-cloud/v1/pure_water_goods_combo/${id}`,
    method: 'GET'
  });
};

/**
 * 新增套餐组合
 * POST /v1/pure_water_goods_combo
 * @param {Object} data - { name, rootDepartId, childIds: [...], sort }
 */
export const addCombo = (data) => {
  return request({
    url: `${baseUrl}/joins-sales-cloud/v1/pure_water_goods_combo`,
    method: 'POST',
    data
  });
};

/**
 * 修改套餐组合（全量替换）
 * PUT /v1/pure_water_goods_combo
 * @param {Object} data - { id, name, rootDepartId, childIds: [...], sort }
 */
export const updateCombo = (data) => {
  return request({
    url: `${baseUrl}/joins-sales-cloud/v1/pure_water_goods_combo`,
    method: 'PUT',
    data
  });
};

/**
 * 删除套餐组合（软删）
 * DELETE /v1/pure_water_goods_combo/{id}
 * @param {Number|String} id
 */
export const delCombo = (id) => {
  return request({
    url: `${baseUrl}/joins-sales-cloud/v1/pure_water_goods_combo/${id}`,
    method: 'DELETE'
  });
};

// ────────────────────────────────────────────────────
// 套餐阶段工期配置（套餐阶段工期配置接口文档.md v1.0）
// ────────────────────────────────────────────────────

/**
 * 查询套餐阶段工期明细（按 sort 升序，未配置返回空数组）
 * GET /v1/pure_water_goods_combo/stage_schedule/{comboId}
 * @param {Number|String} comboId - 套餐ID
 */
export const getStageSchedule = (comboId) => {
  return request({
    url: `${baseUrl}/joins-sales-cloud/v1/pure_water_goods_combo/stage_schedule/${comboId}`,
    method: 'GET'
  });
};

/**
 * 全量覆盖保存套餐阶段工期明细（需登录态）
 * PUT /v1/pure_water_goods_combo/stage_schedule
 * 保存成功后端回写 combo.required_days = Σ耗时 + Σ间隔
 * @param {Object} data - { comboId, stages: [{ goodsId, stageName, requiredDays, intervalDays, sort }] }
 */
export const saveStageSchedule = (data) => {
  return request({
    url: `${baseUrl}/joins-sales-cloud/v1/pure_water_goods_combo/stage_schedule`,
    method: 'PUT',
    data
  });
};

// ────────────────────────────────────────────────────
// 辅助接口（防水项目 & 阶段）
// ────────────────────────────────────────────────────

/**
 * 获取防水项目列表（用于选择项目加载阶段）
 * @param {Object} params - { page, size, name }
 */
export const findProjectList = (params = {}) => {
  params.type = 'WATERPROOF_PROJECT';
  return findWaterProofDynamic(params);
};

/**
 * 获取指定防水项目的阶段列表（阶段 ID 即 combo 的 childIds）
 * GET /joins-sales-cloud/v1/pure_water_goods/engineering_goods/{projectId}
 * @param {String} projectId
 */
export const findStageListByProjectId = (projectId) => {
  return findWaterProofDynamicId(projectId);
};
